import * as React from "react";
import styled, { css, keyframes } from "styled-components";
import { WeekReport } from "src/database/models/GoalSummary";

interface Props {
  summary?: WeekReport;
}

const pipTurnOn = keyframes`
  from {
    background-color: var(--box-color-contrast);
  }
  to {
    background-color: var(--theme-color-primary);
  }
`;

const PIP_DURATION_S = 0.45;
const PIP_STAGGER_S = 0.055;

export function GoalAccordionSummary({ summary }: Props) {
  /** Completed count after last commit — compared to `summary.completed` to detect newly filled pips. */
  const priorCompletedRef = React.useRef<number | null>(null);

  React.useLayoutEffect(() => {
    if (summary) priorCompletedRef.current = summary.completed;
  }, [summary?.completed, summary?.total]);

  const priorCompleted = priorCompletedRef.current;

  return (
    <Container>
      <Progress>
        {summary &&
          Array.from({ length: summary.total }, (_, index) => {
            const on = index < summary.completed;
            const justLit = on && priorCompleted !== null && index >= priorCompleted;
            return <Pip key={index} $on={on} $justLit={justLit} $index={index} />;
          })}
      </Progress>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Progress = styled.div`
  display: flex;
  gap: 2px;
  width: 100%;
  height: 10px;
`;

const Pip = styled.div<{ $on: boolean; $justLit: boolean; $index: number }>`
  width: 10px;
  height: 100%;
  border-radius: 2px;

  ${({ $on, $justLit, $index }) =>
    !$on
      ? css`
          background-color: var(--box-color-contrast);
        `
      : $justLit
        ? css`
            background-color: var(--box-color-contrast);
            animation: ${pipTurnOn} ${PIP_DURATION_S}s cubic-bezier(0.33, 1.06, 0.64, 1) forwards;
            animation-delay: ${$index * PIP_STAGGER_S}s;
          `
        : css`
            background-color: var(--theme-color-primary);
          `}
`;
